import React, { useCallback, useEffect, useState } from "react";
import { useFormik } from "formik";
import { Link } from "react-router-dom";
import * as Yup from "yup";
import "./login-form.scss";
import { getRedirectResult, signInWithPopup, signInWithRedirect } from "firebase/auth";
import { auth, googleProvider } from "../firebase";
import { useDispatch } from "react-redux";
import { setUser } from "../../../Slices/User/userDataSlice";
import { toast } from "react-toastify";
import { authErrorToMessage } from "../errorMessages";
import { UnauthorizedError } from "../../../utils/customError";
import request from "../../../utils/useApi";
import { setCurrency } from "../../../utils/context";
import {
  exchangeGoogleSession,
  shouldFallbackToGoogleRedirect,
} from "../googleAuth";

const LoginForm = ({ onLoginSuccess, goToRegister = () => {}, goToResetPassword = () => {} }) => {
  const dispatch = useDispatch();

  const [isSocialLogining, setIsSocialLogining] = useState(false);

  const finishGoogleLogin = useCallback(async (firebaseUser) => {
    const session = await exchangeGoogleSession(firebaseUser);
    dispatch(setUser(session));
    localStorage.setItem("teleport_token", session.token);
    setCurrency(session.user.currency || "USD");
    onLoginSuccess();
    toast.success("Logged in successfully!");
  }, [dispatch, onLoginSuccess]);

  useEffect(() => {
    let active = true;
    getRedirectResult(auth)
      .then(async (result) => {
        if (!active || !result?.user) return;
        setIsSocialLogining(true);
        await finishGoogleLogin(result.user);
      })
      .catch((error) => {
        if (!active) return;
        console.error("Google redirect sign-in failed:", error);
        toast.error(
          error?.response?.data?.message ||
          authErrorToMessage(error?.code) ||
          error?.message ||
          "Unable to sign in with Google. Please try again."
        );
      })
      .finally(() => {
        if (active) setIsSocialLogining(false);
      });

    return () => {
      active = false;
    };
  }, [finishGoogleLogin]);

  const handleGoogleLogin = async () => {
    if (isSocialLogining) {
      return;
    }

    setIsSocialLogining(true);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      await finishGoogleLogin(result.user);
    } catch (error) {
      console.error("Google sign-in failed:", error);
      if (shouldFallbackToGoogleRedirect(error)) {
        await signInWithRedirect(auth, googleProvider);
        return;
      }
      const msg =
        error?.response?.data?.message ||
        authErrorToMessage(error?.code) ||
        error?.message ||
        "Unable to sign in. Please try again.";
      toast.error(msg);
    } finally {
      setIsSocialLogining(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Invalid email address")
        .required("Email is required"),
      password: Yup.string()
        .min(6, "Password must be at least 6 characters")
        .required("Password is required"),
    }),
    onSubmit: async (values) => {
      try {
        let response = await request.post("/auth/login", {
          email: values.email,
          password: values.password,
        });
        if (response.status !== 200) {
          throw new UnauthorizedError(response.data.message);
        }

        dispatch(setUser(response.data.data));
        localStorage.setItem("teleport_token", response.data.data.token);
        const currency = response.data.data.user.currency || 'USD';
        setCurrency(currency)
        onLoginSuccess();
        toast.success("Logged in successfully!");
      } catch (error) {
        console.error("Login error:", error);

        if (error instanceof UnauthorizedError) {
          toast.error(error.message || "Login failed. Please try again.");
          return;
        }

        const apiMsg = error?.response?.data?.message;
        const fbMsg = authErrorToMessage(error?.code);

        toast.error(apiMsg || fbMsg || "Login failed. Please try again.");
      }
    },
  });

  return (
    <div className="login-section">
      <form onSubmit={formik.handleSubmit} className="login-form">
        <div className="heading">
          <h2>Log In</h2>
          <p>Just a few taps to take off!</p>
        </div>

        <div className="content">
          <div className="input-wrap">
            <label>Email</label>
            <input
              type="email"
              name="email"
              placeholder="Enter email"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.email && formik.errors.email && (
              <div className="error">{formik.errors.email}</div>
            )}
          </div>

          <div className="input-wrap">
            <label>Password</label>
            <input
              type="password"
              name="password"
              placeholder="Enter password"
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.password && formik.errors.password && (
              <div className="error">{formik.errors.password}</div>
            )}
          </div>

          <div className="forgot-password">
            <Link to="/resetPassword" onClick={goToResetPassword}>Forgot password?</Link>
          </div>

          <button className="custom-btn" type="submit">
            Log In
          </button>

          <div className="social-auth">
            <div className="divider">
              <span>Or Login with</span>
            </div>
            <div className="social-icons">
              <button
                className="social-btn google"
                type="button"
                onClick={handleGoogleLogin}
                disabled={isSocialLogining}
                aria-label="Continue with Google"
              >
                <img src="/images/icons/google.svg" alt="" />
              </button>
            </div>

            <p className="already-have">
              Don’t have an account?{" "}
              <Link to="/register" onClick={goToRegister}>
                Register Now
              </Link>
            </p>
          </div>
        </div>
      </form>
    </div>
  );
};

export default LoginForm;
